import React, { useState } from "react";
import Select from "@mui/material/Select";
import { groupBy, prop } from "ramda";
import { Chip, Typography, MenuItem, ListSubheader } from "@mui/material";
import { SelectFieldStyled } from "./select-field.style";
import { isSomething } from "../../../utils/utils";
import { sortByCategoryAndLabel } from "./select.utils";
import { DownIconSelect } from "../../../utils/svg-components";

const SelectField = (props) => {
  const {
    name,
    options,
    isRequired,
    value,
    onChange,
    multiple,
    label,
    disabled,
    placeholder,
    noBorder = false,
    boldValue = false,
    isTransparent = false,
    onCrossClick,
    isGrouped = false,
    error,
    closeOnSelect = false,
  } = props;

  // Controlled open state for the select menu
  const [open, setOpen] = useState(false);

  // Grouping logic for grouped options (if needed)
  const groupByValuesNew = () => {
    const safeOptions = Array.isArray(options) ? options : [];
    // Include users missing serviceGroup (previously dropped entirely from the menu).
    const validOptions = safeOptions.map((item) => ({
      ...item,
      category:
        item.category != null && String(item.category).trim() !== ""
          ? item.category
          : "Other",
    }));
    const groupByCategory = groupBy(prop("category"));
    const groupedData = groupByCategory(validOptions);

    const data = Object.keys(groupedData)
      .map((category) => {
        const categoryOptions = groupedData[category] || [];
        return [{ label: category, type: "category" }, ...categoryOptions];
      })
      .flat();

    const sortedData = sortByCategoryAndLabel(data);
    return sortedData;
  };

  const updatedCategoryOptions = isGrouped ? groupByValuesNew() : [];

  // Custom icon component that handles its own onClick to open the select
  const CustomIcon = (iconProps) => (
    <div
      style={{
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.6 : 1,
      }}
      onClick={(e) => {
        e.stopPropagation();
        if (!disabled) setOpen((prev) => !prev);
      }}
    >
      <DownIconSelect {...iconProps} />
    </div>
  );

  const handleChange = (event) => {
    onChange?.(event);
    if (multiple && closeOnSelect) {
      setOpen(false);
    }
  };

  return (
    <SelectFieldStyled isTransparent={isTransparent}>
      {label && (
        <div className="label">
          <Typography variant="body1">
            {label}
            {isRequired && "*"}
          </Typography>
        </div>
      )}
      <Select
        id={name}
        displayEmpty
        value={multiple ? value || [] : value}
        multiple={multiple}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        open={open}
        onOpen={() => {
          if (!disabled) setOpen(true);
        }}
        onClose={() => setOpen(false)}
        IconComponent={CustomIcon}
        MenuProps={{
          PaperProps: {
            style: { maxHeight: 450 },
          },
        }}
        renderValue={(selected) => {
          if (!isSomething(selected)) {
            return (
              <Typography
                className="placeholder-style"
                variant="body1"
                color="primary"
                sx={{ opacity: "0.6" }}
              >
                {placeholder}
              </Typography>
            );
          }

          return Array.isArray(selected) ? (
            <div className="chips-container">
              {selected
                .filter((select) => select)
                .map((select, chipIndex) => {
                  const optionLabel = options?.find(
                    (option) => option?.value === select
                  )?.label;
                  return (
                    optionLabel && (
                      <Chip
                        key={`${select}-chip-${chipIndex}`}
                        label={optionLabel}
                        onMouseDown={(event) => event.stopPropagation()}
                        onDelete={() => onCrossClick && onCrossClick(select)}
                      />
                    )
                  );
                })}
            </div>
          ) : (
            <Typography variant="body1" color="primary">
              {options?.find((option) => option?.value === selected)?.label}
            </Typography>
          );
        }}
      >
        {isGrouped &&
          updatedCategoryOptions?.map((x) =>
            x?.type === "category" ? (
              <ListSubheader
                key={`subheader-${x?.label}`}
                sx={{
                  fontSize: "12px",
                  height: "32px",
                  color: "#301D35",
                  fontWeight: "bold",
                }}
              >
                {x?.label}
              </ListSubheader>
            ) : (
              <MenuItem key={x?.id ?? x?.value} value={x?.value}>
                <Typography variant="body1" color="primary">
                  {x?.label}
                </Typography>
              </MenuItem>
            )
          )}

        {!isGrouped &&
          (Array.isArray(options) ? options : []).map((option) => (
            <MenuItem key={option?.id ?? option?.value} value={option?.value}>
              <Typography variant="body1" color="primary">
                {option?.label}
              </Typography>
            </MenuItem>
          ))}
      </Select>
      {error && (
        <Typography variant="caption" color="error">
          {error.message}
        </Typography>
      )}
    </SelectFieldStyled>
  );
};

export default SelectField;
